import React, { useState, useMemo } from 'react';
import { useProject } from '@/contexts/ProjectContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Bell, 
  CheckCircle, 
  AlertTriangle, 
  Clock, 
  User, 
  MessageCircle,
  Calendar,
  X,
  Settings,
  MarkAsUnread
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { TaskStatus, TaskPriority } from '@/types';

interface Notification {
  id: string;
  type: 'task_assigned' | 'task_deadline' | 'task_completed' | 'project_update' | 'comment_added' | 'mention' | 'system';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  userId?: string;
  taskId?: string;
  projectId?: string;
  actionUrl?: string;
}

export const NotificationCenter: React.FC = () => {
  const { tasks, projects } = useProject();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  // Mock users
  const mockUsers = [
    { id: '1', name: 'Admin User', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face' },
    { id: '2', name: 'Manager User', avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b865?w=32&h=32&fit=crop&crop=face' },
    { id: '3', name: 'Collaborator User', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=32&h=32&fit=crop&crop=face' }
  ];

  // Generate notifications based on tasks and projects
  const [notifications] = useState<Notification[]>([
    {
      id: '1',
      type: 'task_assigned',
      title: 'Nova tarefa atribuída',
      message: 'Você foi atribuído à tarefa "Implementação Frontend"',
      timestamp: new Date(Date.now() - 1000 * 60 * 15), // 15 minutes ago
      read: false,
      priority: 'high',
      userId: '1',
      taskId: '3'
    },
    {
      id: '2',
      type: 'task_deadline',
      title: 'Prazo se aproximando',
      message: 'A tarefa "Análise de Requisitos" vence em 2 horas',
      timestamp: new Date(Date.now() - 1000 * 60 * 30), // 30 minutes ago
      read: false,
      priority: 'urgent',
      taskId: '1'
    },
    {
      id: '3',
      type: 'comment_added',
      title: 'Novo comentário',
      message: 'Manager User comentou na tarefa "Criação de Wireframes"',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 1), // 1 hour ago
      read: true,
      priority: 'medium',
      userId: '2',
      taskId: '2'
    },
    {
      id: '4',
      type: 'project_update',
      title: 'Projeto atualizado',
      message: 'O status do projeto "Website Redesign" foi alterado',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2), // 2 hours ago
      read: true,
      priority: 'medium',
      projectId: '1'
    },
    {
      id: '5',
      type: 'task_completed',
      title: 'Tarefa concluída',
      message: 'Collaborator User concluiu "Definição de Arquitetura"',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 4), // 4 hours ago
      read: true,
      priority: 'low',
      userId: '3',
      taskId: '5'
    },
    {
      id: '6',
      type: 'mention',
      title: 'Você foi mencionado',
      message: 'Admin User mencionou você em um comentário',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 6), // 6 hours ago
      read: false,
      priority: 'high',
      userId: '1'
    },
    {
      id: '7',
      type: 'system',
      title: 'Backup concluído',
      message: 'Backup automático do sistema foi concluído com sucesso',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24), // 1 day ago
      read: true,
      priority: 'low'
    },
    {
      id: '8',
      type: 'task_deadline',
      title: 'Tarefa atrasada',
      message: 'A tarefa "Testes de Usabilidade" está atrasada',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 8), // 8 hours ago
      read: false,
      priority: 'urgent',
      taskId: '4'
    }
  ]);

  const unreadCount = useMemo(() => {
    return notifications.filter(n => !n.read).length;
  }, [notifications]);

  const sortedNotifications = useMemo(() => {
    return [...notifications].sort((a, b) => {
      // Unread first
      if (a.read !== b.read) {
        return a.read ? 1 : -1;
      }
      // Then by priority
      const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
      if (a.priority !== b.priority) {
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      }
      // Finally by timestamp
      return b.timestamp.getTime() - a.timestamp.getTime();
    });
  }, [notifications]);

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'task_assigned': return <User className="w-4 h-4 text-blue-500" />;
      case 'task_deadline': return <Clock className="w-4 h-4 text-orange-500" />;
      case 'task_completed': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'project_update': return <Calendar className="w-4 h-4 text-purple-500" />;
      case 'comment_added': return <MessageCircle className="w-4 h-4 text-indigo-500" />;
      case 'mention': return <User className="w-4 h-4 text-teal-500" />;
      case 'system': return <Settings className="w-4 h-4 text-gray-500" />;
      default: return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  const getPriorityColor = (priority: Notification['priority']) => {
    switch (priority) {
      case 'urgent': return 'border-l-red-500 bg-red-50';
      case 'high': return 'border-l-orange-500 bg-orange-50';
      case 'medium': return 'border-l-yellow-500 bg-yellow-50';
      case 'low': return 'border-l-green-500 bg-green-50';
      default: return 'border-l-gray-500 bg-gray-50';
    }
  };

  const markAsRead = (notificationId: string) => {
    console.log('Marking notification as read:', notificationId);
    // In a real app, this would update the state or make an API call
  };

  const markAllAsRead = () => {
    console.log('Marking all notifications as read');
    // In a real app, this would update all notifications
  };

  const deleteNotification = (notificationId: string) => {
    console.log('Deleting notification:', notificationId);
    // In a real app, this would remove the notification
  };

  const getUserById = (userId?: string) => {
    if (!userId) return null;
    return mockUsers.find(u => u.id === userId);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative">
          <Bell className="w-4 h-4" />
          {unreadCount > 0 && (
            <Badge className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center p-0 bg-red-500">
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Notificações</CardTitle>
              <div className="flex items-center space-x-2">
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={markAllAsRead}>
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Marcar todas
                  </Button>
                )}
                <Button variant="ghost" size="sm">
                  <Settings className="w-4 h-4" />
                </Button>
              </div>
            </div>
            {unreadCount > 0 && (
              <p className="text-sm text-gray-600">
                {unreadCount} {unreadCount === 1 ? 'notificação não lida' : 'notificações não lidas'}
              </p>
            )}
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-96">
              <div className="space-y-1">
                {sortedNotifications.length > 0 ? (
                  sortedNotifications.map((notification) => {
                    const notificationUser = getUserById(notification.userId);
                    
                    return (
                      <div
                        key={notification.id}
                        className={`p-3 border-l-4 ${getPriorityColor(notification.priority)} ${
                          !notification.read ? 'bg-opacity-100' : 'bg-opacity-50'
                        } hover:bg-opacity-75 transition-colors cursor-pointer`}
                        onClick={() => !notification.read && markAsRead(notification.id)}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 mt-1">
                            {getNotificationIcon(notification.type)}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <h4 className={`text-sm font-medium ${
                                !notification.read ? 'text-gray-900' : 'text-gray-700'
                              }`}>
                                {notification.title}
                              </h4>
                              <div className="flex items-center space-x-1">
                                {!notification.read && (
                                  <div className="w-2 h-2 bg-blue-500 rounded-full" />
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="w-4 h-4 p-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteNotification(notification.id);
                                  }}
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                            
                            <p className={`text-sm mt-1 ${
                              !notification.read ? 'text-gray-800' : 'text-gray-600'
                            }`}>
                              {notification.message}
                            </p>
                            
                            <div className="flex items-center justify-between mt-2">
                              <div className="flex items-center space-x-2">
                                {notificationUser && (
                                  <Avatar className="w-4 h-4">
                                    <AvatarImage src={notificationUser.avatar} alt={notificationUser.name} />
                                    <AvatarFallback className="text-xs">
                                      {notificationUser.name.split(' ').map(n => n[0]).join('')}
                                    </AvatarFallback>
                                  </Avatar>
                                )}
                                <span className="text-xs text-gray-500">
                                  {formatDistanceToNow(notification.timestamp, { 
                                    addSuffix: true, 
                                    locale: ptBR 
                                  })}
                                </span>
                              </div>
                              
                              <Badge 
                                variant="secondary" 
                                className={`text-xs ${
                                  notification.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                                  notification.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                                  notification.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-green-100 text-green-800'
                                }`}
                              >
                                {notification.priority === 'urgent' ? 'Urgente' :
                                 notification.priority === 'high' ? 'Alta' :
                                 notification.priority === 'medium' ? 'Média' :
                                 'Baixa'}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Nenhuma notificação</p>
                    <p className="text-sm mt-1">Você está em dia!</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
          
          {sortedNotifications.length > 0 && (
            <div className="p-3 border-t bg-gray-50">
              <Button variant="ghost" size="sm" className="w-full">
                Ver todas as notificações
              </Button>
            </div>
          )}
        </Card>
      </PopoverContent>
    </Popover>
  );
};